import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Sparkle, Robot, ChartBar, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { PageLoader, Stat, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import api, { money } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

export default function OrgDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/organizer/dashboard").then((r) => setData(r.data));
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(load, []);
  if (!data) return <PageLoader />;
  const a = data.analytics;
  const approved = data.profile_status === "approved";

  return (
    <div>
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="overline mb-1">Welcome back</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">{user?.name}</h1>

      {!approved && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-start justify-between gap-3 flex-wrap" data-testid="onboarding-banner">
          <div className="flex items-start gap-3">
            <WarningCircle className="text-amber-400 mt-0.5" size={22} />
            <div>
              <div className="font-semibold">Complete onboarding to publish events</div>
              <p className="text-sm text-muted-foreground">You can create and preview drafts now, but publishing needs admin approval. Current status: <StatusBadge status={data.profile_status} /></p>
            </div>
          </div>
          <Button asChild className="rounded-full"><Link to="/organizer/onboarding">Continue onboarding <ArrowRight className="ml-1.5" /></Link></Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Revenue" value={money(a.revenue)} accent />
        <Stat label="Tickets Sold" value={a.tickets_sold} />
        <Stat label="Capacity Filled" value={`${a.capacity_pct}%`} />
        <Stat label="Events" value={a.events} />
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        <QuickAction to="/organizer/events/new" icon={PlusCircle} title="Create Event" desc="Manual or AI-assisted" />
        <QuickAction to="/organizer/package-builder" icon={Sparkle} title="AI Package Builder" desc="Boost your sales" />
        <QuickAction to="/organizer/copilot" icon={Robot} title="AI Copilot" desc="Ask about your sales" />
      </div>

      <h2 className="font-heading text-xl font-semibold mb-3">Your events</h2>
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {a.event_summaries.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No events yet. <Link to="/organizer/events/new" className="text-primary">Create one</Link>.</div>
        ) : a.event_summaries.map((e) => (
          <Link key={e.id} to={`/organizer/events/${e.id}/edit`} className="flex items-center justify-between p-4 hover:bg-muted/40">
            <div className="flex items-center gap-3">
              <StatusBadge status={e.status} />
              <span className="font-medium">{e.title}</span>
            </div>
            <div className="text-sm text-muted-foreground">{e.sold}/{e.capacity} sold</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="p-5 rounded-xl border border-border bg-card card-lift hover:shadow-lg">
      <Icon size={24} className="text-primary" weight="fill" />
      <div className="font-heading font-semibold mt-3">{title}</div>
      <div className="text-sm text-muted-foreground">{desc}</div>
    </Link>
  );
}
